/**
 * Brick & Board — Custom Deck Builder (storefront)
 * Exact card-name match against Shopify catalog via Ajax search + product.js
 */
(function () {
  'use strict';

  var ROOT_SEL = '[data-bb-deck-builder]';
  var CONCURRENCY = 3;
  var SUGGEST_LIMIT = 10;
  var MAX_QTY_FALLBACK = 40;

  var CONDITIONS = [
    { id: 'NM', label: 'NM', patterns: [/^near\s*mint$/i, /^nm$/i] },
    { id: 'LP', label: 'LP', patterns: [/^lightly\s*played$/i, /^lp$/i, /^excellent$/i] },
    { id: 'MP', label: 'MP', patterns: [/^moderately\s*played$/i, /^mp$/i, /^played$/i] },
    { id: 'HP', label: 'HP', patterns: [/^heavily\s*played$/i, /^hp$/i, /^poor$/i] },
    { id: 'DMG', label: 'Damaged', patterns: [/^damaged$/i, /^dmg$/i] }
  ];

  var SECTION_RE =
    /^(commander|deck|main|mainboard|main\s*board|sideboard|side\s*board|sb|maybeboard|maybe\s*board|wishlist|companion|transit|tokens?|lands?|creatures?|instants?|sorceries?|artifacts?|enchantments?|planeswalkers?|battles?)$/i;

  var SKIP_SECTIONS = /^(maybeboard|maybe\s*board|wishlist|tokens?)$/i;
  var SIDEBOARD_SECTIONS = /^(sideboard|side\s*board|sb)$/i;

  var TREATMENT_RE =
    /^(borderless|extended\s*art|showcase|retro\s*frame|serialized|etched|foil|non[\s-]*foil|full\s*art|textless|promo|alternate\s*art|universes\s*beyond|oil\s*slick|step[\s-]*and[\s-]*compleat|surge\s*foil|confetti\s*foil|rainbow\s*foil|gilded|raised\s*foil|fracture\s*foil|neon\s*ink|phyrexian|japanese|korean|chinese|russian|german|french|italian|spanish|portuguese|pool\s*party\s*foil|comic[\s-]*book|anime|stamped|prerelease|bundle|buy[\s-]*a[\s-]*box|fnm|judge|sdcc).*$/i;

  var SET_CODE_RE = /^[A-Za-z0-9]{2,6}-\d{1,4}[A-Za-z]?$/;
  var COLLECTOR_ONLY_RE = /^\d{1,5}[A-Za-z]?$/;
  var SET_COLLECTOR_INLINE_RE = /^([A-Za-z0-9]{2,6})-(\d{1,4}[A-Za-z]?)$/;

  function $(sel, root) {
    return (root || document).querySelector(sel);
  }

  function $all(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }

  function money(cents) {
    var n = (Number(cents) || 0) / 100;
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD'
      }).format(n);
    } catch (e) {
      return '$' + n.toFixed(2);
    }
  }

  function rootPath() {
    return (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  }

  function sleep(ms) {
    return new Promise(function (r) {
      setTimeout(r, ms);
    });
  }

  function normalizeWhitespace(s) {
    return String(s || '')
      .replace(/\u00a0/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function normalizeNameKey(s) {
    return normalizeWhitespace(s)
      .toLowerCase()
      .replace(/[’']/g, "'")
      .replace(/\s*\/\/\s*/g, ' // ');
  }

  function stripOuterQuotes(s) {
    s = normalizeWhitespace(s);
    if (
      (s.charAt(0) === '"' && s.charAt(s.length - 1) === '"') ||
      (s.charAt(0) === "'" && s.charAt(s.length - 1) === "'")
    ) {
      return s.slice(1, -1).trim();
    }
    return s;
  }

  /** Extract card name from Storepass-style Shopify titles. */
  function cardNameFromProductTitle(title) {
    var t = normalizeWhitespace(title);
    var dash = t.lastIndexOf(' - ');
    var left = dash >= 0 ? t.slice(0, dash) : t;

    // Peel trailing parentheticals that are set/collector/treatment
    var changed = true;
    while (changed) {
      changed = false;
      var m = left.match(/^(.*)\s*\(([^)]+)\)\s*$/);
      if (!m) break;
      var inner = normalizeWhitespace(m[2]);
      if (
        SET_CODE_RE.test(inner) ||
        COLLECTOR_ONLY_RE.test(inner) ||
        TREATMENT_RE.test(inner) ||
        /foil/i.test(inner)
      ) {
        left = normalizeWhitespace(m[1]);
        changed = true;
      } else {
        break;
      }
    }
    return normalizeWhitespace(left);
  }

  function parseSetCollectorFromTitle(title) {
    var t = normalizeWhitespace(title);
    var setCode = null;
    var collector = null;
    var foil = /foil/i.test(t) && !/non[\s-]*foil/i.test(t);
    var re = /\(([^)]+)\)/g;
    var m;
    while ((m = re.exec(t))) {
      var inner = normalizeWhitespace(m[1]);
      var sc = inner.match(SET_COLLECTOR_INLINE_RE);
      if (sc) {
        setCode = sc[1].toUpperCase();
        collector = sc[2].toUpperCase();
      } else if (COLLECTOR_ONLY_RE.test(inner) && !collector) {
        collector = inner.toUpperCase();
      }
    }
    return { setCode: setCode, collector: collector, foil: foil };
  }

  function isMtgSingleProduct(product) {
    var type = String(product.type || product.product_type || '').toLowerCase();
    var tags = product.tags;
    var tagStr = Array.isArray(tags) ? tags.join(',').toLowerCase() : String(tags || '').toLowerCase();
    var title = String(product.title || '').toLowerCase();

    if (/board\s*game|accessory|accessories|sealed|booster|bundle|pre[\s-]*con|deck\s*box|sleeve|playmat|dice|miniature|warhammer|lego/.test(type)) {
      return false;
    }
    if (/board\s*game|accessory|sealed product|booster box|booster pack/.test(tagStr)) {
      return false;
    }
    if (/mtg\s*single|magic.+single/.test(type)) return true;
    if (/type:\s*single/.test(tagStr) && /magic|mtg/.test(tagStr + ' ' + type + ' ' + title)) return true;
    if (/brand:\s*magic/.test(tagStr) && /type:\s*single/.test(tagStr)) return true;
    // Storepass titles look like "Name (SET-123) - Set"
    if (/\([A-Za-z0-9]{2,6}-\d{1,4}/.test(product.title || '') && !/lego|warhammer/.test(title)) {
      return true;
    }
    return false;
  }

  function conditionIdFromVariantTitle(title) {
    var t = normalizeWhitespace(title || '');
    for (var i = 0; i < CONDITIONS.length; i++) {
      for (var j = 0; j < CONDITIONS[i].patterns.length; j++) {
        if (CONDITIONS[i].patterns[j].test(t)) return CONDITIONS[i].id;
      }
    }
    return null;
  }

  function productIsFoil(product) {
    var tags = product.tags;
    var tagStr = Array.isArray(tags) ? tags.join(',').toLowerCase() : String(tags || '').toLowerCase();
    if (/printing:\s*foil/.test(tagStr) && !/non[\s-]*foil/.test(tagStr)) return true;
    if (/printing:\s*non[\s-]*foil/.test(tagStr)) return false;
    var meta = parseSetCollectorFromTitle(product.title || '');
    return !!meta.foil;
  }

  /* ------------------------------------------------------------------ *
   * Multi-game support
   * Each game knows how to (a) recognise its singles, (b) pull the card
   * name out of a Storepass title, (c) read set/number/code metadata,
   * (d) read finish (foil/holo), and (e) parse its deck-list lines.
   * Matching stays exact on the card-name part of the title.
   * ------------------------------------------------------------------ */

  var OTHER_GAME_SINGLE_TYPES = /^(pokemon single|pokemon japanese single|lorcana single|one piece single|star wars: unlimited single|gundam single)$/i;

  function tagList(product) {
    var tags = product.tags;
    if (Array.isArray(tags)) return tags.map(String);
    return String(tags || '')
      .split(',')
      .map(function (t) {
        return t.trim();
      })
      .filter(Boolean);
  }

  /** Value of a "Key: value" tag, or null. */
  function tagValue(product, key) {
    var k = key.toLowerCase() + ':';
    var tags = tagList(product);
    for (var i = 0; i < tags.length; i++) {
      var t = tags[i];
      if (t.toLowerCase().indexOf(k) === 0) return normalizeWhitespace(t.slice(k.length));
    }
    return null;
  }

  function productType(product) {
    return normalizeWhitespace(product.type || product.product_type || '').toLowerCase();
  }

  function stripDiacritics(s) {
    try {
      return String(s).normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    } catch (e) {
      return String(s);
    }
  }

  /** Looser (still exact) key for non-MTG games: accents, dash styles, quote styles. */
  function looseNameKey(s) {
    return stripDiacritics(normalizeNameKey(s))
      .replace(/[‘’`´]/g, "'")
      .replace(/[“”]/g, '"')
      .replace(/\s*[–—]\s*/g, ' - ')
      .replace(/\s+-\s+/g, ' - ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  /** Peel every trailing "(...)" / "[...]" group. */
  function peelTrailingGroups(s) {
    var prev;
    s = normalizeWhitespace(s);
    do {
      prev = s;
      s = s.replace(/\s*(\([^()]*\)|\[[^\[\]]*\])\s*$/, '').trim();
    } while (s !== prev && s);
    return s || prev;
  }

  /** Left side of "Name ... - Set Name [Foil]" (last " - "). */
  function leftOfSetSeparator(title) {
    var t = normalizeWhitespace(title);
    var dash = t.lastIndexOf(' - ');
    return dash >= 0 ? t.slice(0, dash) : t;
  }

  /** Normalise a collector number: "038" -> "38", "TG08" -> "TG8", "57a" -> "57A". */
  function normCollector(n) {
    if (n == null) return null;
    var s = String(n).toUpperCase().trim();
    s = s.split('/')[0];
    var m = s.match(/^([A-Z-]*?)0*(\d+)([A-Z]?)$/);
    if (!m) return s || null;
    return m[1] + m[2] + m[3];
  }

  /** Same card number? "57" also matches promo "SWSH057"/"SVP057" style numbers. */
  function collectorEq(want, got) {
    var a = normCollector(want);
    var b = normCollector(got);
    if (!a || !b) return false;
    if (a === b) return true;
    var da = a.match(/^([A-Z-]*)(\d+[A-Z]?)$/);
    var db = b.match(/^([A-Z-]*)(\d+[A-Z]?)$/);
    if (!da || !db) return false;
    // Only when the deck list gave a bare number (no letter prefix).
    return !da[1] && da[2] === db[2];
  }

  function setKey(s) {
    return stripDiacritics(String(s || ''))
      .toLowerCase()
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9]+/g, '');
  }

  /* ---------- Pokémon ---------- */

  // Pokémon TCG Live / Limitless set codes -> set name as it appears in the
  // store's "Set:" tag after stripping the series prefix (e.g. "SV02: ").
  var POKEMON_SETS = {
    // Mega Evolution
    MEG: 'Mega Evolution', ME1: 'Mega Evolution', PFL: 'Phantasmal Flames', ME2: 'Phantasmal Flames',
    ASC: 'Ascended Heroes', POR: 'Perfect Order', ME3: 'Perfect Order', CRI: 'Chaos Rising', ME4: 'Chaos Rising',
    PBL: 'Pitch Black', ME5: 'Pitch Black', MEP: 'Mega Evolution Promo', '30C': '30th Celebration',
    // Scarlet & Violet
    SVI: 'Scarlet & Violet Base Set', SV1: 'Scarlet & Violet Base Set', PAL: 'Paldea Evolved', SV2: 'Paldea Evolved',
    OBF: 'Obsidian Flames', SV3: 'Obsidian Flames', MEW: 'Scarlet & Violet 151', PAR: 'Paradox Rift', SV4: 'Paradox Rift',
    PAF: 'Paldean Fates', TEF: 'Temporal Forces', SV5: 'Temporal Forces', TWM: 'Twilight Masquerade', SV6: 'Twilight Masquerade',
    SFA: 'Shrouded Fable', SCR: 'Stellar Crown', SV7: 'Stellar Crown', SSP: 'Surging Sparks', SV8: 'Surging Sparks',
    PRE: 'Prismatic Evolutions', JTG: 'Journey Together', SV9: 'Journey Together', DRI: 'Destined Rivals', SV10: 'Destined Rivals',
    BLK: 'Black Bolt', WHT: 'White Flare', SVP: 'Scarlet & Violet Promo Cards', 'PR-SV': 'Scarlet & Violet Promo Cards',
    // Sword & Shield
    SSH: 'Sword & Shield Base Set', RCL: 'Rebel Clash', DAA: 'Darkness Ablaze', CPA: "Champion's Path", VIV: 'Vivid Voltage',
    SHF: 'Shining Fates', BST: 'Battle Styles', CRE: 'Chilling Reign', EVS: 'Evolving Skies', CEL: 'Celebrations',
    FST: 'Fusion Strike', BRS: 'Brilliant Stars', ASR: 'Astral Radiance', PGO: 'Pokemon GO', LOR: 'Lost Origin',
    SIT: 'Silver Tempest', CRZ: 'Crown Zenith', SWSHP: 'Sword & Shield Promo Cards', 'PR-SW': 'Sword & Shield Promo Cards',
    // Sun & Moon
    SUM: 'SM Base Set', GRI: 'Guardians Rising', BUS: 'Burning Shadows', SLG: 'Shining Legends', CIN: 'Crimson Invasion',
    UPR: 'Ultra Prism', FLI: 'Forbidden Light', CES: 'Celestial Storm', DRM: 'Dragon Majesty', LOT: 'Lost Thunder',
    TEU: 'Team Up', UNB: 'Unbroken Bonds', UNM: 'Unified Minds', HIF: 'Hidden Fates', CEC: 'Cosmic Eclipse',
    SMP: 'SM Promos', 'PR-SM': 'SM Promos',
    // XY
    XY: 'XY Base Set', FLF: 'Flashfire', FFI: 'Furious Fists', PHF: 'Phantom Forces', PRC: 'Primal Clash',
    ROS: 'Roaring Skies', AOR: 'Ancient Origins', BKT: 'BREAKthrough', BKP: 'BREAKpoint', GEN: 'Generations',
    FCO: 'Fates Collide', STS: 'Steam Siege', EVO: 'Evolutions', XYP: 'XY Promos', 'PR-XY': 'XY Promos',
    // Black & White
    BLW: 'Black and White', EPO: 'Emerging Powers', NVI: 'Noble Victories', NXD: 'Next Destinies', DEX: 'Dark Explorers',
    DRX: 'Dragons Exalted', DRV: 'Dragon Vault', BCR: 'Boundaries Crossed', PLS: 'Plasma Storm', PLF: 'Plasma Freeze',
    PLB: 'Plasma Blast', LTR: 'Legendary Treasures',
    // Classic
    BS: 'Base Set', JU: 'Jungle', FO: 'Fossil', B2: 'Base Set 2', TR: 'Team Rocket', G1: 'Gym Heroes', G2: 'Gym Challenge',
    N1: 'Neo Genesis', N2: 'Neo Discovery', N3: 'Neo Revelation', N4: 'Neo Destiny', LC: 'Legendary Collection',
    CL: 'Call of Legends'
  };

  var POKEMON_SET_KEYS = (function () {
    var m = Object.create(null);
    Object.keys(POKEMON_SETS).forEach(function (k) {
      m[k] = setKey(POKEMON_SETS[k]);
    });
    return m;
  })();

  /** "SV02: Paldea Evolved" -> "Paldea Evolved"; "Brilliant Stars Trainer Gallery" -> "Brilliant Stars". */
  function pokemonSetCore(setName) {
    var s = normalizeWhitespace(setName || '');
    s = s.replace(/^(?:SV|SWSH|SM|XY|ME|BW)\d{0,2}\s*[:\-]\s*/i, '');
    s = s.replace(/\s*:\s*(?:Galarian Gallery|Shiny Vault|Radiant Collection|Classic Collection)$/i, '');
    s = s.replace(/\s+Trainer Gallery$/i, '');
    return s;
  }

  var ENERGY_TYPES = 'grass|fire|water|lightning|psychic|fighting|darkness|metal|fairy';
  var ENERGY_SYMBOLS = { G: 'grass', R: 'fire', W: 'water', L: 'lightning', P: 'psychic', F: 'fighting', D: 'darkness', M: 'metal', Y: 'fairy' };
  var BASIC_ENERGY_RE = new RegExp('^(?:basic )?(' + ENERGY_TYPES + ') energy$', 'i');

  function pokemonNameKey(s) {
    var k = looseNameKey(s).replace(/\{([a-z])\}/g, function (m, c) {
      return ENERGY_SYMBOLS[c.toUpperCase()] || m;
    });
    var e = k.match(BASIC_ENERGY_RE);
    if (e) return e[1].toLowerCase() + ' energy';
    return k;
  }

  var PKM_NUM_TOKEN = /\s+(?:[A-Za-z]{0,6}\d+[A-Za-z]?)(?:\/[A-Za-z]{0,6}\d+[A-Za-z]?)*$/;

  /**
   * "Pikachu ex 057/198  - Holofoil SV01 ... - Double Rare" -> "Pikachu ex"
   * "Pikachu (Movie Promo) 004  WoTC Promo - Promo"         -> "Pikachu"
   * "Boss's Orders [Corbeau] 256/217  - Holofoil ..."       -> "Boss's Orders"
   */
  function pokemonCardName(title) {
    var t = String(title || '').replace(/\u00a0/g, ' ');
    var dbl = t.indexOf('  ');
    var head = dbl >= 0 ? t.slice(0, dbl) : leftOfSetSeparator(t);
    head = normalizeWhitespace(head);
    var prev;
    do {
      prev = head;
      head = head.replace(PKM_NUM_TOKEN, '').trim();
      head = head.replace(/\/[A-Za-z]{0,6}\d+[A-Za-z]?$/, '').trim();
      head = head.replace(/\s*(\([^()]*\)|\[[^\[\]]*\])\s*$/, '').trim();
      head = head.replace(/\s+-\s+[A-Za-z]{0,6}\d+[A-Za-z]?(?:\/[A-Za-z]{0,6}\d+[A-Za-z]?)*$/, '').trim();
    } while (head && head !== prev);
    return head || prev;
  }

  function pokemonMeta(product) {
    var title = normalizeWhitespace(product.title || '');
    var number = tagValue(product, 'Number');
    if (!number) {
      var raw = String(product.title || '');
      var dbl = raw.indexOf('  ');
      var head = normalizeWhitespace(dbl >= 0 ? raw.slice(0, dbl) : title);
      var m = head.match(/\s([A-Za-z]{0,6}\d+[A-Za-z]?)(?:\/[A-Za-z]{0,6}\d+[A-Za-z]?)*$/);
      if (m) number = m[1];
    }
    var setName = tagValue(product, 'Set') || '';
    return { setName: setName, setCore: setKey(pokemonSetCore(setName)), collector: number };
  }

  function pokemonFinish(product) {
    var p = (tagValue(product, 'Printing') || '').toLowerCase();
    if (!p) {
      var t = String(product.title || '').toLowerCase();
      if (/reverse holo/.test(t)) return 'reverse';
      if (/holofoil/.test(t)) return 'holo';
      return 'normal';
    }
    if (/reverse/.test(p)) return 'reverse';
    if (/holo|foil/.test(p)) return 'holo';
    return 'normal';
  }

  /* ---------- Star Wars: Unlimited ---------- */

  var SWU_SETS = {
    SOR: 'Spark of Rebellion', SHD: 'Shadows of the Galaxy', TWI: 'Twilight of the Republic',
    JTL: 'Jump to Lightspeed', LOF: 'Legends of the Force', SEC: 'Secrets of Power', IBH: 'Intro Battle: Hoth'
  };

  function swuMeta(product) {
    var number = tagValue(product, 'Number');
    if (!number) {
      var m = leftOfSetSeparator(product.title || '').match(/\((\d{1,4})(?:\/\d+)?\)\s*$/);
      if (m) number = m[1];
    }
    var setName = (tagValue(product, 'Set') || '').replace(/\s+-\s+.*$/, '');
    return { setName: setName, setCore: setKey(setName), collector: number };
  }

  /* ---------- One Piece / Gundam (code-numbered games) ---------- */

  var CARD_CODE_RE = /\b([A-Z]{1,4}\d{0,2}-\d{3}[A-Z]?)\b/;

  function codeGameCardName(title) {
    var left = peelTrailingGroups(leftOfSetSeparator(title));
    // "Nami - OP01-016 (Alternate Art)" -> "Nami"
    left = left.replace(/\s+-\s+[A-Z]{1,4}\d{0,2}-\d{3}[A-Z]?$/, '').trim();
    return peelTrailingGroups(left);
  }

  // Gundam card names can legitimately contain parentheses ("Unicorn Gundam
  // (Unicorn Mode)" vs "(Destroy Mode)"), so only peel rarity/promo tags.
  var GUNDAM_TREATMENT_RE =
    /^(?:[CURL]R?\+{1,2}|LR\+{0,2}|SP|P|parallel|alternate art|alt art|.*\b(?:event|pack|promo|promotion|tournament|participant|winner|champion|prerelease|pre-release|judge|anniversary|exclusive|prize|box|edition|beta|foil|holo|holofoil|campaign|celebration|battle|release|premium)\b.*)$/i;

  function gundamCardName(title) {
    var left = leftOfSetSeparator(title).replace(/\s*\([A-Z]{1,4}\d{0,2}-\d{3}[A-Z]?\)/g, '');
    left = normalizeWhitespace(left);
    var m;
    while ((m = left.match(/^(.*\S)\s*\(([^()]*)\)$/)) && GUNDAM_TREATMENT_RE.test(normalizeWhitespace(m[2]))) {
      left = normalizeWhitespace(m[1]);
    }
    return left.replace(/\s+-\s+[A-Z]{1,4}\d{0,2}-\d{3}[A-Z]?$/, '').trim();
  }

  function codeGameMeta(product) {
    var code = tagValue(product, 'Number');
    if (!code || !CARD_CODE_RE.test(code.toUpperCase())) {
      var re = /\(([A-Z]{1,4}\d{0,2}-\d{3}[A-Z]?)\)/g;
      var m;
      code = null;
      while ((m = re.exec(product.title || ''))) code = m[1];
    }
    code = code ? code.toUpperCase() : null;
    var setPart = code ? code.split('-')[0] : null;
    return { setName: tagValue(product, 'Set') || '', setCode: setPart, collector: code, code: code };
  }

  /* ---------- Lorcana ---------- */

  function lorcanaMeta(product) {
    var number = tagValue(product, 'Number');
    if (!number) {
      var m = leftOfSetSeparator(product.title || '').match(/\(([^()]*\d[^()]*)\)\s*$/);
      if (m) number = m[1];
    }
    return { setName: tagValue(product, 'Set') || '', setCore: setKey(tagValue(product, 'Set')), collector: number };
  }

  /** Foil if the Printing tag (or title) says anything but Normal/Non-Foil. */
  function genericFinish(product) {
    var p = (tagValue(product, 'Printing') || '').toLowerCase();
    if (p) return /^(normal|non[\s-]*foil|unlimited|1st edition)$/.test(p) ? 'normal' : 'foil';
    return /\b(foil|holofoil)\s*$/i.test(product.title || '') ? 'foil' : 'normal';
  }

  function finishLabelFromTag(product, fallback) {
    var p = tagValue(product, 'Printing');
    return p && !/^(normal|non[\s-]*foil)$/i.test(p) ? p : fallback;
  }

  function isTypeSingle(product, typeRe, brandRe) {
    var type = productType(product);
    if (typeRe.test(type)) return true;
    if (type && !/single/.test(type)) return false;
    var brand = (tagValue(product, 'Brand') || '').toLowerCase();
    var ttag = (tagValue(product, 'Type') || '').toLowerCase();
    return brandRe.test(brand) && ttag === 'single' && !/graded|sealed|japanese/.test(type);
  }

  var GAMES = {
    mtg: {
      id: 'mtg',
      label: 'Magic: The Gathering',
      placeholder: '4 Sol Ring\n1 Counterspell (MH2) 267\nSB: 2 Negate',
      hint:
        'Supported formats: <code>4 Card Name</code>, <code>4x Card Name</code>, <code>Card Name (SET) 123</code>, Moxfield / Archidekt / Scryfall / Arena / MTGO exports. Section headers like Commander, Deck, and Sideboard are recognized.',
      finishOptions: [
        ['either', 'Either'],
        ['nonfoil', 'Nonfoil only'],
        ['foil', 'Foil only']
      ],
      finishTitle: 'Foil'
    },
    pokemon: {
      id: 'pokemon',
      label: 'Pokémon',
      placeholder: 'Pokémon: 12\n4 Pikachu ex SSP 57\n3 Charizard ex OBF 125\nTrainer: 30\n4 Boss\'s Orders PAL 172\nEnergy: 8\n8 Basic {R} Energy SVE 2',
      hint:
        'Paste a Pokémon TCG Live or Limitless export (<code>4 Pikachu ex SSP 57</code>), or plain <code>4 Card Name</code>. When a set code and number are given we match that exact printing first. Pokémon / Trainer / Energy headers are recognized. Basic Energy is skipped.',
      finishOptions: [
        ['either', 'Any'],
        ['nonfoil', 'Normal (non-holo)'],
        ['holo', 'Holo'],
        ['reverse', 'Reverse Holo']
      ],
      finishTitle: 'Finish'
    },
    lorcana: {
      id: 'lorcana',
      label: 'Disney Lorcana',
      placeholder: '4 Ariel - Spectacular Singer\n4 Mickey Mouse - Brave Little Tailor\n2 A Whole New World',
      hint:
        'Paste a Dreamborn, Inktable or Lorcanito export, one card per line: <code>4 Name - Version</code> (e.g. <code>4 Ariel - Spectacular Singer</code>). Include the version — “Ariel” alone won’t match “Ariel - Spectacular Singer”.',
      finishOptions: [
        ['either', 'Any'],
        ['nonfoil', 'Normal only'],
        ['foil', 'Foil only (Cold Foil / Holofoil)']
      ],
      finishTitle: 'Finish'
    },
    onepiece: {
      id: 'onepiece',
      label: 'One Piece',
      placeholder: '1xOP01-001\n4xOP01-016\n4 Nami\n4 Trafalgar Law (ST02-009)',
      hint:
        'Paste a OPTCG Sim / One Piece Top Decks export (<code>4xOP01-016</code>) or names (<code>4 Nami</code>). Card codes match every printing of that card, including alternate arts.',
      finishOptions: [
        ['either', 'Any'],
        ['nonfoil', 'Normal only'],
        ['foil', 'Foil only']
      ],
      finishTitle: 'Finish'
    },
    swu: {
      id: 'swu',
      label: 'Star Wars: Unlimited',
      placeholder: 'Leader\n1 Padmé Amidala - What Do You Have to Hide?\nDeck\n3 C-3PO - Anything I Might Do?\n2 Supreme Council Aide\n3 SEC 603',
      hint:
        'Paste a SWUDB / Melee list: <code>3 Name - Subtitle</code>, <code>3 Name, Subtitle</code>, or set + number like <code>3 SEC 603</code> / <code>SEC_603</code>. Leader, Base, Deck and Sideboard headers are recognized.',
      finishOptions: [
        ['either', 'Any'],
        ['nonfoil', 'Normal only'],
        ['foil', 'Foil only']
      ],
      finishTitle: 'Finish'
    },
    gundam: {
      id: 'gundam',
      label: 'Gundam',
      placeholder: '4xGD01-004\n4 Guncannon\n2 Unicorn Gundam (Unicorn Mode)',
      hint:
        'Paste card codes (<code>4xGD01-004</code>) or names (<code>4 Guncannon</code>). Card codes match every printing of that card.',
      finishOptions: [
        ['either', 'Any'],
        ['nonfoil', 'Normal only'],
        ['foil', 'Holofoil only']
      ],
      finishTitle: 'Finish'
    }
  };

  GAMES.mtg.isSingle = function (p) {
    var type = productType(p);
    if (OTHER_GAME_SINGLE_TYPES.test(type)) return false;
    var brand = (tagValue(p, 'Brand') || '').toLowerCase();
    if (brand && !/magic|mtg/.test(brand) && !/mtg|magic/.test(type)) return false;
    return isMtgSingleProduct(p);
  };
  GAMES.mtg.cardName = cardNameFromProductTitle;
  GAMES.mtg.nameKey = normalizeNameKey;
  GAMES.mtg.finish = function (p) {
    return productIsFoil(p) ? 'foil' : 'normal';
  };
  GAMES.mtg.finishLabel = function (p, f) {
    return f === 'foil' ? 'Foil' : '';
  };

  // World Championship deck cards (gold border, not tournament legal) are never offered.
  var POKEMON_WORLDS_RE = /world\s*championships?\s*deck|\bworlds?\s+deck/i;
  GAMES.pokemon.isSingle = function (p) {
    if (POKEMON_WORLDS_RE.test(p.title || '') || POKEMON_WORLDS_RE.test(tagValue(p, 'Set') || '')) return false;
    return isTypeSingle(p, /^pokemon single$/, /pok[eé]mon/);
  };
  GAMES.pokemon.strictPrinting = true;
  var PKM_TRAINER_SECTION = /^(trainers?|energy|energies)$/i;
  var PKM_TRAINER_TYPE = /^(trainer|supporter|item|tool|pok[eé]mon tool|stadium|energy|special energy|ace spec)\b/i;
  /** Trainer / Special Energy lines may fall back to another printing of the same name. */
  GAMES.pokemon.fallbackEligible = function (line, products) {
    if (PKM_TRAINER_SECTION.test(line.section || '')) return true;
    var types = (products || []).map(function (p) {
      return tagValue(p, 'Card Type') || '';
    }).filter(Boolean);
    if (!types.length) return /\benergy$/i.test(line.name || '');
    return types.every(function (t) {
      return PKM_TRAINER_TYPE.test(t);
    });
  };
  GAMES.pokemon.cardName = pokemonCardName;
  GAMES.pokemon.nameKey = pokemonNameKey;
  GAMES.pokemon.meta = pokemonMeta;
  GAMES.pokemon.setMatches = function (meta, code) {
    var want = POKEMON_SET_KEYS[String(code || '').toUpperCase()];
    return !!want && meta.setCore === want;
  };
  GAMES.pokemon.setLabel = function (code) {
    return POKEMON_SETS[String(code || '').toUpperCase()] || null;
  };
  GAMES.pokemon.finish = pokemonFinish;
  GAMES.pokemon.finishLabel = function (p, f) {
    return f === 'reverse' ? 'Reverse Holo' : f === 'holo' ? 'Holo' : '';
  };
  GAMES.pokemon.skipLine = function (line) {
    return BASIC_ENERGY_RE.test(pokemonNameKey(line.name)) ? 'basic_energy' : null;
  };

  GAMES.lorcana.isSingle = function (p) {
    return isTypeSingle(p, /^lorcana single$/, /lorcana/);
  };
  GAMES.lorcana.cardName = function (title) {
    return peelTrailingGroups(leftOfSetSeparator(title));
  };
  GAMES.lorcana.nameKey = looseNameKey;
  GAMES.lorcana.meta = lorcanaMeta;
  GAMES.lorcana.finish = genericFinish;
  GAMES.lorcana.finishLabel = function (p, f) {
    return f === 'foil' ? finishLabelFromTag(p, 'Foil') : '';
  };

  GAMES.onepiece.isSingle = function (p) {
    return isTypeSingle(p, /^one piece single$/, /one piece/);
  };
  GAMES.onepiece.cardName = codeGameCardName;
  GAMES.onepiece.nameKey = looseNameKey;
  GAMES.onepiece.meta = codeGameMeta;
  GAMES.onepiece.usesCodes = true;
  GAMES.onepiece.finish = genericFinish;
  GAMES.onepiece.finishLabel = function (p, f) {
    return f === 'foil' ? 'Foil' : '';
  };

  GAMES.gundam.isSingle = function (p) {
    return isTypeSingle(p, /^gundam single$/, /gundam/);
  };
  GAMES.gundam.cardName = gundamCardName;
  GAMES.gundam.nameKey = looseNameKey;
  GAMES.gundam.meta = codeGameMeta;
  GAMES.gundam.usesCodes = true;
  GAMES.gundam.finish = genericFinish;
  GAMES.gundam.finishLabel = function (p, f) {
    return f === 'foil' ? 'Holofoil' : '';
  };

  GAMES.swu.isSingle = function (p) {
    return isTypeSingle(p, /^star wars: unlimited single$/, /star wars/);
  };
  GAMES.swu.cardName = function (title) {
    return peelTrailingGroups(leftOfSetSeparator(title));
  };
  GAMES.swu.nameKey = function (s) {
    // SWUDB sometimes writes "Name, Subtitle" or "Name | Subtitle".
    return looseNameKey(String(s || '').replace(/\s*\|\s*/g, ' - '));
  };
  GAMES.swu.meta = swuMeta;
  GAMES.swu.setMatches = function (meta, code) {
    var want = SWU_SETS[String(code || '').toUpperCase()];
    return !!want && meta.setCore === setKey(want);
  };
  GAMES.swu.setLabel = function (code) {
    return SWU_SETS[String(code || '').toUpperCase()] || null;
  };
  GAMES.swu.finish = genericFinish;
  GAMES.swu.finishLabel = function (p, f) {
    return f === 'foil' ? 'Foil' : '';
  };

  function getGame(id) {
    return GAMES[id] || GAMES.mtg;
  }

  var GENERIC_SECTION_RE =
    /^(leaders?|bases?|deck|main|main\s*deck|mainboard|sideboard|side\s*board|sb|characters?|events?|stages?|units?|ground\s*units?|space\s*units?|upgrades?|pilots?|commands?|resources?|tokens?|actions?|items?|songs?|locations?|don!*|maybeboard|maybe\s*board|wishlist)\s*(?:\(\d+\))?\s*:?\s*\d*$/i;
  var POKEMON_SECTION_RE = /^(pok[eé]mon|trainers?|energy|energies|total\s*cards)\s*(?:\(\d+\))?\s*[:\-]?\s*\d*$/i;

  /** Section header for non-MTG games, or null. */
  function genericHeader(raw, game) {
    var h = normalizeWhitespace(raw);
    if (game.id === 'pokemon' && POKEMON_SECTION_RE.test(h)) {
      return /^total/i.test(h) ? { skipLine: true } : { name: h.replace(/[\s:(\-].*$/, '').toLowerCase() };
    }
    if (GENERIC_SECTION_RE.test(h)) {
      var name = h.replace(/\s*(?:\(\d+\))?\s*:?\s*\d*$/, '').toLowerCase();
      return { name: name };
    }
    return null;
  }

  /** Quantity prefix for non-MTG lines: "4 X", "4x X", "4xOP01-016", "4 | X", "* 4 X". */
  function splitQtyGeneric(s) {
    s = s.replace(/^\*\s*/, '');
    var m = s.match(/^(\d+)\s*(?:[xX×]\s*|\s+|\s*\|\s*|\s*[-–—]\s+)(\S.*)$/);
    if (m) return { qty: parseInt(m[1], 10), rest: m[2], explicit: true };
    m = s.match(/^(.+?)\s+[xX×]\s*(\d+)$/);
    if (m) return { qty: parseInt(m[2], 10), rest: m[1], explicit: true };
    return { qty: 1, rest: s, explicit: false };
  }

  function parseCardLineGeneric(raw, section, sideboard, game) {
    var s = stripOuterQuotes(raw);
    var q = splitQtyGeneric(s);
    var qty = q.qty;
    var rest = normalizeWhitespace(q.rest);
    if (!rest || !isFinite(qty) || qty < 1) return null;
    // "Resource" / "Base" are real card names when a quantity is given.
    if (!q.explicit && genericHeader(rest, game)) return null;

    var setCode = null;
    var collector = null;
    var code = null;

    if (game.id === 'pokemon') {
      // PTCGL: "Pikachu ex SVI 57", "Iono PAL 185", "Pikachu PR-SV 27", "Arven SVI 166a"
      var ptcgl = rest.match(/^(.+?)\s+([A-Z0-9]{2,5}(?:-[A-Z]{2,4})?)\s+([A-Z]{0,5}\d{1,4}[a-z]?)$/);
      // "Pikachu ex (SVI) 57" / "Pikachu ex [SVI] 57" / "Pikachu ex (SVI 57)"
      var paren = rest.match(/^(.+?)\s*[\[(]([A-Z0-9]{2,5}(?:-[A-Z]{2,4})?)[\])]\s*([A-Z]{0,5}\d{1,4}[a-z]?)?$/) ||
        rest.match(/^(.+?)\s*\(([A-Z0-9]{2,5}(?:-[A-Z]{2,4})?)\s+([A-Z]{0,5}\d{1,4}[a-z]?)\)$/);
      // Older PTCGL energy lines: "Fire Energy Energy 2", "Psychic Energy SMEnergy 5"
      var oldEnergy = rest.match(/^(.+?\bEnergy)\s+[A-Za-z]*Energy\s+\d+$/i);
      if (oldEnergy) {
        rest = oldEnergy[1];
      } else if (ptcgl && /[A-Z]/.test(ptcgl[2]) && !/^(EX|GX|V|VMAX|VSTAR|BREAK|LV)$/.test(ptcgl[2])) {
        rest = ptcgl[1];
        setCode = ptcgl[2];
        collector = ptcgl[3].toUpperCase();
      } else if (paren && /[A-Z]/.test(paren[2])) {
        rest = paren[1];
        setCode = paren[2];
        if (paren[3]) collector = paren[3].toUpperCase();
      } else {
        // Trailing collector only: "Pikachu 057/198"
        var numOnly = rest.match(/^(.+?)\s+#?(\d{1,3}\/\d{1,3})$/);
        if (numOnly) {
          rest = numOnly[1];
          collector = numOnly[2].split('/')[0];
        }
      }
    } else if (game.usesCodes) {
      var cm = rest.match(CARD_CODE_RE);
      if (cm) {
        code = cm[1].toUpperCase();
        rest = normalizeWhitespace(
          rest
            .replace(cm[0], ' ')
            .replace(/[\[(]\s*[\])]/g, ' ')
            .replace(/^\s*[-–—|:]\s*|\s*[-–—|:]\s*$/g, ' ')
        );
        setCode = code.split('-')[0];
        collector = code;
      }
    } else if (game.id === 'swu') {
      // "SEC 603", "SEC_603", "SEC-603", "Name (SEC) 603", "Name SEC 603"
      var idOnly = rest.match(/^([A-Z]{3})[\s_\-#]+(\d{1,4})$/);
      var withName = rest.match(/^(.+?)\s*[\[(]?\b([A-Z]{3})[\])]?[\s_\-#]+(\d{1,4})$/);
      if (idOnly && SWU_SETS[idOnly[1]]) {
        rest = '';
        setCode = idOnly[1];
        collector = idOnly[2];
      } else if (withName && SWU_SETS[withName[2]]) {
        rest = withName[1];
        setCode = withName[2];
        collector = withName[3];
      }
      rest = rest.replace(/\s*\|\s*/g, ' - ');
      // "Luke Skywalker, Faithful Friend" -> "Luke Skywalker - Faithful Friend"
      if (rest.indexOf(' - ') === -1 && /, /.test(rest)) rest = rest.replace(/,\s+/, ' - ');
    } else if (game.id === 'lorcana') {
      var lnum = rest.match(/^(.+?)\s*\((\d{1,3})(?:\/\d{1,3})?\)$/);
      if (lnum) {
        rest = lnum[1];
        collector = lnum[2];
      }
    }

    rest = stripOuterQuotes(normalizeWhitespace(rest).replace(/\s*\((?:foil|cold foil|holo|holofoil|reverse holo)\)\s*$/i, ''));
    if (!rest && !code && !(setCode && collector)) return null;
    var displayName = rest || code || setCode + ' ' + collector;

    var line = {
      name: rest,
      displayName: displayName,
      qty: qty,
      setCode: setCode,
      collector: collector,
      code: code,
      codeOnly: !rest,
      section: section,
      sideboard: !!sideboard
    };
    var skip = game.skipLine && rest ? game.skipLine(line) : null;
    if (skip) line.skip = skip;
    return line;
  }

  function parseDeckListGeneric(text, includeSideboard, game) {
    var lines = String(text || '').replace(/\r\n/g, '\n').split('\n');
    var section = 'main';
    var sideboard = false;
    var skip = false;
    var out = [];
    for (var i = 0; i < lines.length; i++) {
      var raw = normalizeWhitespace(lines[i]);
      if (!raw) continue;
      if (/^\/\//.test(raw) || /^#/.test(raw)) continue;
      var sbInline = raw.match(/^SB:\s*(.+)$/i);
      if (sbInline) {
        if (!includeSideboard) continue;
        var parsedSb = parseCardLineGeneric(sbInline[1], 'sideboard', true, game);
        if (parsedSb) out.push(parsedSb);
        continue;
      }
      var hdr = !/^\*?\s*\d/.test(raw) ? genericHeader(raw, game) : null;
      if (hdr) {
        if (hdr.skipLine) continue;
        section = hdr.name;
        sideboard = SIDEBOARD_SECTIONS.test(section);
        skip = /^(maybeboard|maybe\s*board|wishlist)$/.test(section);
        continue;
      }
      if (skip) continue;
      if (sideboard && !includeSideboard) continue;
      var parsed = parseCardLineGeneric(raw, section, sideboard, game);
      if (parsed) out.push(parsed);
    }
    var map = Object.create(null);
    var merged = [];
    out.forEach(function (line) {
      var key = [game.nameKey(line.name || ''), line.code || '', line.setCode || '', normCollector(line.collector) || '', line.sideboard ? 'sb' : 'main'].join('|');
      if (map[key]) {
        map[key].qty += line.qty;
      } else {
        map[key] = line;
        merged.push(line);
      }
    });
    return merged;
  }

  /** Does this product's metadata match the deck line's set/number/code? */
  function printingScore(product, line, game) {
    if (game.id === 'mtg') return null; // MTG keeps its original scoring
    var meta = game.meta(product);
    var score = 0;
    if (game.usesCodes) {
      if (line.code && meta.code === line.code) score += 150;
    } else {
      if (line.setCode && game.setMatches && game.setMatches(meta, line.setCode)) score += 100;
      if (line.collector && collectorEq(line.collector, meta.collector)) score += 50;
    }
    if (product.available) score += 5;
    return score;
  }

  /** Product is the card on this line: exact name, or (code games) exact code; (SWU) exact set+number for id-only lines. */
  function lineMatchesProduct(product, line, game) {
    if (!game.isSingle(product)) return false;
    if (game.usesCodes && line.code) {
      return codeGameMeta(product).code === line.code;
    }
    if (line.codeOnly) {
      if (game.id === 'swu' && line.setCode && line.collector) {
        var m = swuMeta(product);
        return game.setMatches(m, line.setCode) && collectorEq(line.collector, m.collector);
      }
      return false;
    }
    return game.nameKey(game.cardName(product.title || '')) === game.nameKey(line.name);
  }

  /**
   * Parse a deck list into lines.
   * @returns {{ name, qty, setCode, collector, section, sideboard }[]}
   */
  function parseDeckList(text, includeSideboard, gameId) {
    if (gameId && gameId !== 'mtg') return parseDeckListGeneric(text, includeSideboard, getGame(gameId));
    var lines = String(text || '').replace(/\r\n/g, '\n').split('\n');
    var section = 'main';
    var sideboard = false;
    var out = [];
    var skip = false;

    for (var i = 0; i < lines.length; i++) {
      var raw = normalizeWhitespace(lines[i]);
      if (!raw) continue;
      if (/^\/\//.test(raw) || /^#/.test(raw)) continue;

      // "SB: 2 Negate" inline
      var sbInline = raw.match(/^SB:\s*(.+)$/i);
      if (sbInline) {
        if (!includeSideboard) continue;
        var parsedSb = parseCardLine(sbInline[1], 'sideboard', true);
        if (parsedSb) out.push(parsedSb);
        continue;
      }

      var header = raw.replace(/:$/, '');
      if (SECTION_RE.test(header) && !/\d/.test(raw.split(/\s+/)[0] || '')) {
        // Avoid treating "1 Forest" style as header; headers usually have no leading qty
        if (!/^\d/.test(raw)) {
          section = header.toLowerCase();
          sideboard = SIDEBOARD_SECTIONS.test(header);
          skip = SKIP_SECTIONS.test(header);
          continue;
        }
      }

      if (skip) continue;
      if (sideboard && !includeSideboard) continue;

      var parsed = parseCardLine(raw, section, sideboard);
      if (parsed) out.push(parsed);
    }

    // Deduplicate by name+set+collector+sideboard
    var map = Object.create(null);
    var merged = [];
    out.forEach(function (line) {
      var key = [normalizeNameKey(line.name), line.setCode || '', line.collector || '', line.sideboard ? 'sb' : 'main'].join('|');
      if (map[key]) {
        map[key].qty += line.qty;
      } else {
        map[key] = line;
        merged.push(line);
      }
    });
    return merged;
  }

  function parseCardLine(raw, section, sideboard) {
    var s = stripOuterQuotes(raw);
    // MTGA/MTGO export sometimes has trailing *FOIL*
    s = s.replace(/\*F\*/gi, '').replace(/\*FOIL\*/gi, '');

    var qty = 1;
    var rest = s;

    // qty-first: 4x Name | 4 Name | 4x Name | 4- Name
    var qtyFirst = s.match(/^(\d+)\s*[xX×]?\s+(.+)$/);
    var qtyDash = s.match(/^(\d+)\s*[-–—]\s*(.+)$/);
    // name-first: Name x4 | Name 4
    var nameFirst = s.match(/^(.+?)\s+[xX×]\s*(\d+)$/);
    var nameTrailingQty = null;

    if (qtyFirst) {
      qty = parseInt(qtyFirst[1], 10);
      rest = qtyFirst[2];
    } else if (qtyDash) {
      qty = parseInt(qtyDash[1], 10);
      rest = qtyDash[2];
    } else if (nameFirst) {
      rest = nameFirst[1];
      qty = parseInt(nameFirst[2], 10);
    } else {
      // "Name 4" only if last token is small int and name looks like a card
      nameTrailingQty = s.match(/^(.+?)\s+(\d{1,2})$/);
      if (nameTrailingQty && !/\(/.test(nameTrailingQty[2])) {
        // Don't steal collector numbers that aren't in parens at end without set — risky.
        // Only treat as qty if no set markers present.
        if (!/[\[(]/.test(s)) {
          rest = nameTrailingQty[1];
          qty = parseInt(nameTrailingQty[2], 10);
        }
      }
    }

    if (!rest || !isFinite(qty) || qty < 1) return null;
    if (SECTION_RE.test(rest.replace(/:$/, ''))) return null;

    var setCode = null;
    var collector = null;

    // Arena: Name (SET) 123
    var arena = rest.match(/^(.+?)\s*\(([A-Za-z0-9]{2,6})\)\s+(\d{1,4}[A-Za-z]?)\s*$/);
    // Bracket: Name [SET] 123 or Name [SET]
    var bracket = rest.match(/^(.+?)\s*\[([A-Za-z0-9]{2,6})\](?:\s+(\d{1,4}[A-Za-z]?))?\s*$/);
    // Moxfield-ish: Name (SET) 123 already covered; also Name SET 123
    var bare = rest.match(/^(.+?)\s+([A-Za-z0-9]{2,6})\s+(\d{1,4}[A-Za-z]?)\s*$/);

    if (arena) {
      rest = arena[1];
      setCode = arena[2].toUpperCase();
      collector = arena[3].toUpperCase();
    } else if (bracket) {
      rest = bracket[1];
      setCode = bracket[2].toUpperCase();
      if (bracket[3]) collector = bracket[3].toUpperCase();
    } else if (bare && !/^(the|a|an|of|to|and)$/i.test(bare[2])) {
      // Only accept if set-looking token is all caps-ish or known length
      if (/^[A-Za-z0-9]{2,6}$/.test(bare[2]) && /[A-Za-z]/.test(bare[2])) {
        rest = bare[1];
        setCode = bare[2].toUpperCase();
        collector = bare[3].toUpperCase();
      }
    }

    // Strip trailing foil markers from name
    rest = rest.replace(/\s*\((?:foil|etched|surge foil)\)\s*$/i, '').trim();
    rest = stripOuterQuotes(rest);
    if (!rest) return null;

    return {
      name: rest,
      qty: qty,
      setCode: setCode,
      collector: collector,
      section: section,
      sideboard: !!sideboard
    };
  }

  function mapPool(items, limit, iterator) {
    var i = 0;
    var results = new Array(items.length);
    var workers = [];
    function next() {
      if (i >= items.length) return Promise.resolve();
      var idx = i++;
      return Promise.resolve(iterator(items[idx], idx)).then(function (res) {
        results[idx] = res;
        return next();
      });
    }
    for (var w = 0; w < Math.min(limit, items.length); w++) workers.push(next());
    return Promise.all(workers).then(function () {
      return results;
    });
  }

  async function fetchJson(url, attempt) {
    attempt = attempt || 0;
    var res = await fetch(url, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    });
    if (res.status === 429) {
      var wait = parseInt(res.headers.get('Retry-After') || '2', 10);
      if (attempt < 5) {
        await sleep((isFinite(wait) ? wait : 2) * 1000);
        return fetchJson(url, attempt + 1);
      }
    }
    if (!res.ok) throw new Error('Request failed (' + res.status + ')');
    var ct = res.headers.get('content-type') || '';
    if (ct.indexOf('application/json') === -1) {
      // Some themes return HTML for search.json — treat as empty
      var text = await res.text();
      try {
        return JSON.parse(text);
      } catch (e) {
        return null;
      }
    }
    return res.json();
  }

  async function suggestProducts(query) {
    var q = encodeURIComponent(query);
    var url =
      rootPath() +
      'search/suggest.json?q=' +
      q +
      '&resources[type]=product&resources[limit]=' +
      SUGGEST_LIMIT +
      '&resources[options][unavailable_products]=last';
    var data = await fetchJson(url);
    if (!data || !data.resources || !data.resources.results) return [];
    return data.resources.results.products || [];
  }

  /** Fallback: scrape search HTML for product handles when suggest caps at 10. */
  async function searchHandlesFromHtml(query, nameKey) {
    var url = rootPath() + 'search?q=' + encodeURIComponent(query) + '&type=product';
    var res = await fetch(url, { credentials: 'same-origin' });
    if (!res.ok) return [];
    var html = await res.text();
    var re = /\/products\/([a-z0-9][a-z0-9\-]+)/gi;
    var seen = Object.create(null);
    var handles = [];
    var m;
    var slugHint = nameKey
      .replace(/[^a-z0-9]+/gi, '-')
      .replace(/^-|-$/g, '')
      .toLowerCase();
    while ((m = re.exec(html))) {
      var h = m[1].toLowerCase();
      if (seen[h]) continue;
      // Prefer handles that start with slugified card name
      if (slugHint && h.indexOf(slugHint.split('-').slice(0, 3).join('-')) === -1) {
        // still allow if later we verify by title via product.js
        // keep candidates that share first token
        var first = slugHint.split('-')[0];
        if (first && h.indexOf(first) !== 0 && h.indexOf(first) === -1) continue;
      }
      seen[h] = true;
      handles.push(h);
      if (handles.length >= 40) break;
    }
    return handles;
  }

  async function fetchProduct(handle) {
    var url = rootPath() + 'products/' + encodeURIComponent(handle) + '.js';
    return fetchJson(url);
  }

  var STOCK_SECTION_ID = 'bb-stock-json';

  /**
   * Variant inventory via the Section Rendering API
   * (sections/bb-stock-json.liquid rendered in the product's context).
   * Resolves to { variantId: qty } where qty is a number, or null when the
   * variant isn't inventory-tracked (unlimited). Resolves to null if the
   * section is missing or the request fails (stock unknown).
   */
  async function fetchStock(handle, attempt) {
    attempt = attempt || 0;
    var url = rootPath() + 'products/' + encodeURIComponent(handle) + '?section_id=' + STOCK_SECTION_ID;
    try {
      var res = await fetch(url, { credentials: 'same-origin' });
      if (res.status === 429 && attempt < 3) {
        await sleep(2000);
        return fetchStock(handle, attempt + 1);
      }
      if (!res.ok) return null;
      var html = await res.text();
      var m = html.match(/<script[^>]*data-bb-stock[^>]*>([\s\S]*?)<\/script>/i);
      if (!m) return null;
      var data = JSON.parse(m[1]);
      var map = Object.create(null);
      (data.variants || []).forEach(function (v) {
        map[v.id] = v.inventory_quantity == null ? null : Math.max(0, Number(v.inventory_quantity) || 0);
      });
      return map;
    } catch (e) {
      return null;
    }
  }

  /** number = known qty, Infinity = untracked/unlimited, null = unknown */
  function stockFor(product, variant) {
    var map = product.__bbStock;
    if (!map || !(variant.id in map)) return null;
    return map[variant.id] === null ? Infinity : map[variant.id];
  }

  function exactNameMatch(product, wantName) {
    var got = normalizeNameKey(cardNameFromProductTitle(product.title || ''));
    return got === normalizeNameKey(wantName);
  }

  function scorePrinting(product, line, game) {
    if (game && game.id !== 'mtg') return printingScore(product, line, game);
    var meta = parseSetCollectorFromTitle(product.title || '');
    var score = 0;
    if (line.setCode && meta.setCode === line.setCode) score += 100;
    if (line.collector && meta.collector === line.collector) score += 50;
    if (line.setCode && product.handle && product.handle.toUpperCase().indexOf(line.setCode) >= 0) score += 20;
    if (product.available) score += 5;
    return score;
  }

  /** Condition + foil filters only. In-stock is applied in buildCandidateVariants. */
  function variantMatchesFilters(variant, product, state) {
    var cond = conditionIdFromVariantTitle(variant.title || variant.option1 || '');
    if (state.conditions.length && cond && state.conditions.indexOf(cond) === -1) return false;
    if (state.conditions.length && !cond) return false;

    var game = getGame(state.game);
    var finish = game.finish(product);
    if (state.foil === 'foil' && finish === 'normal') return false;
    if (state.foil === 'nonfoil' && finish !== 'normal') return false;
    if (state.foil === 'holo' && finish !== 'holo') return false;
    if (state.foil === 'reverse' && finish !== 'reverse') return false;
    return true;
  }

  function buildCandidateVariants(products, line, state) {
    var list = [];
    var game = getGame(state.game);
    products.forEach(function (p) {
      var finish = game.finish(p);
      (p.variants || []).forEach(function (v) {
        if (!variantMatchesFilters(v, p, state)) return;
        var stock = stockFor(p, v);
        var available = !!v.available && stock !== 0;
        if (state.inStockOnly && !available) return;
        list.push({
          product: p,
          variant: v,
          condition: conditionIdFromVariantTitle(v.title || v.option1 || ''),
          foil: finish !== 'normal',
          finish: finish,
          finishLabel: game.finishLabel(p, finish),
          price: v.price,
          available: available,
          stock: stock,
          image: (p.featured_image && (p.featured_image.src || p.featured_image)) || (p.images && p.images[0]) || null,
          score: scorePrinting(p, line, game)
        });
      });
    });
    if (line.allowFallback) {
      var exactList = list.filter(function (c) {
        return c.product.__bbBest;
      });
      var usable = exactList.some(function (c) {
        return c.available;
      });
      if (!usable) {
        var others = list.filter(function (c) {
          return !c.product.__bbBest;
        });
        if (others.some(function (c) {
          return c.available;
        })) {
          others.forEach(function (c) {
            c.fallback = true;
            c.score = 0; // no "same set" bonus: cheapest in-stock wins
          });
          // Cheapest in-stock first regardless of the sort dropdown's tiebreaks.
          return sortCandidates(others, state.sort);
        }
      }
      list = exactList.length ? exactList : list;
    }
    return sortCandidates(list, state.sort);
  }

  function stockRank(c) {
    if (!c.available) return -1;
    if (c.stock === Infinity) return 1e9;
    return c.stock == null ? 1 : c.stock;
  }

  function sortCandidates(list, sort) {
    var arr = list.slice();
    arr.sort(function (a, b) {
      if (sort === 'price_asc') return a.price - b.price || b.score - a.score;
      if (sort === 'price_desc') return b.price - a.price || b.score - a.score;
      if (sort === 'name') {
        return String(a.product.title).localeCompare(String(b.product.title)) || a.price - b.price;
      }
      if (sort === 'stock') {
        return stockRank(b) - stockRank(a) || a.price - b.price;
      }
      // default: prefer set match then cheapest
      return b.score - a.score || a.price - b.price;
    });
    return arr;
  }

  var COND_ORDER = { NM: 0, LP: 1, MP: 2, HP: 3, DMG: 4 };

  /**
   * Fill `qty` copies cheapest-first across in-stock candidates, respecting each
   * variant's inventory. Requested set/collector printings are used first.
   * `pinnedId` (a variant the shopper clicked) is filled before anything else.
   * Unknown stock counts as 1; any leftover goes on the first unknown-stock pick
   * (marked unverified) so the shopper's qty isn't silently dropped.
   * @returns {{ cand, qty, unverified? }[]}
   */
  function allocate(candidates, line, qty, pinnedId) {
    var wantsPrinting = !!(line.setCode || line.collector);
    function tier(c) {
      if (!wantsPrinting) return 0;
      return c.score >= 100 ? 0 : c.score >= 50 ? 1 : 2;
    }
    var pool = candidates.filter(function (c) {
      return c.available;
    });
    pool.sort(function (a, b) {
      if (pinnedId) {
        if (a.variant.id === pinnedId) return -1;
        if (b.variant.id === pinnedId) return 1;
      }
      return (
        tier(a) - tier(b) ||
        a.price - b.price ||
        (a.condition in COND_ORDER ? COND_ORDER[a.condition] : 9) - (b.condition in COND_ORDER ? COND_ORDER[b.condition] : 9)
      );
    });

    var picks = [];
    var remaining = qty;
    var unknownPick = null;
    for (var i = 0; i < pool.length && remaining > 0; i++) {
      var c = pool[i];
      var cap = c.stock == null ? 1 : c.stock;
      var take = Math.min(cap, remaining);
      if (take <= 0) continue;
      var pick = { cand: c, qty: take };
      picks.push(pick);
      remaining -= take;
      if (c.stock == null && !unknownPick) unknownPick = pick;
    }
    if (remaining > 0 && unknownPick) {
      unknownPick.qty += remaining;
      unknownPick.unverified = true;
    }
    return picks;
  }

  function pickedQty(row) {
    return (row.picks || []).reduce(function (n, p) {
      return n + p.qty;
    }, 0);
  }

  function DeckBuilder(root) {
    this.root = root;
    this.els = {
      textarea: $('[data-bb-db-list]', root),
      findBtn: $('[data-bb-db-find]', root),
      clearBtn: $('[data-bb-db-clear]', root),
      status: $('[data-bb-db-status]', root),
      progress: $('[data-bb-db-progress]', root),
      progressBar: $('[data-bb-db-progress-bar]', root),
      results: $('[data-bb-db-results]', root),
      lines: $('[data-bb-db-lines]', root),
      notfound: $('[data-bb-db-notfound]', root),
      summary: $('[data-bb-db-summary]', root),
      addBtn: $('[data-bb-db-add]', root),
      autoBtn: $('[data-bb-db-autoselect]', root),
      inStock: $('[data-bb-db-instock]', root),
      sideboard: $('[data-bb-db-sideboard]', root),
      sort: $('[data-bb-db-sort]', root),
      foil: $('[data-bb-db-foil]', root),
      total: $('[data-bb-db-total]', root),
      game: $('[data-bb-db-game]', root),
      hint: $('[data-bb-db-hint]', root),
      intro: $('[data-bb-db-intro]', root),
      finishTitle: $('[data-bb-db-finish-title]', root)
    };
    this.applyInitialGame();
    this.lineResults = [];
    this.busy = false;
    this.bind();
  }

  DeckBuilder.prototype.getState = function () {
    var conditions = $all('[data-bb-db-condition]:checked', this.root).map(function (el) {
      return el.value;
    });
    return {
      conditions: conditions,
      inStockOnly: !!(this.els.inStock && this.els.inStock.checked),
      includeSideboard: !!(this.els.sideboard && this.els.sideboard.checked),
      sort: (this.els.sort && this.els.sort.value) || 'price_asc',
      foil: (this.els.foil && this.els.foil.value) || 'either',
      game: this.currentGameId()
    };
  };

  DeckBuilder.prototype.currentGameId = function () {
    var id = (this.els.game && this.els.game.value) || this.root.getAttribute('data-default-game') || 'mtg';
    return GAMES[id] ? id : 'mtg';
  };

  /** ?game=pokemon in the URL wins over the section default. */
  DeckBuilder.prototype.applyInitialGame = function () {
    if (!this.els.game) return;
    var fromUrl = null;
    try {
      fromUrl = new URLSearchParams(window.location.search).get('game');
    } catch (e) {}
    var def = this.root.getAttribute('data-default-game');
    var pick = (fromUrl && GAMES[fromUrl] && fromUrl) || (def && GAMES[def] && def) || this.els.game.value;
    var opt = this.els.game.querySelector('option[value="' + pick + '"]');
    if (opt) this.els.game.value = pick;
    this.applyGameUi();
  };

  /** Placeholder, help text, intro, and finish options for the selected game. */
  DeckBuilder.prototype.applyGameUi = function () {
    var game = getGame(this.currentGameId());
    this.root.setAttribute('data-game', game.id);
    if (this.els.textarea) this.els.textarea.setAttribute('placeholder', game.placeholder);
    if (this.els.hint) this.els.hint.innerHTML = game.hint;
    if (this.els.intro) {
      var attr = 'data-intro-' + game.id;
      var t = this.els.intro.hasAttribute(attr) ? this.els.intro.getAttribute(attr) : '';
      this.els.intro.textContent = t;
      this.els.intro.hidden = !t;
    }
    if (this.els.finishTitle) this.els.finishTitle.textContent = game.finishTitle;
    if (this.els.foil) {
      var prev = this.els.foil.value;
      this.els.foil.innerHTML = game.finishOptions
        .map(function (o) {
          return '<option value="' + o[0] + '">' + escapeHtml(o[1]) + '</option>';
        })
        .join('');
      var keep = game.finishOptions.some(function (o) {
        return o[0] === prev;
      });
      this.els.foil.value = keep ? prev : 'either';
    }
  };

  DeckBuilder.prototype.bind = function () {
    var self = this;
    if (this.els.findBtn) {
      this.els.findBtn.addEventListener('click', function () {
        self.runSearch();
      });
    }
    if (this.els.clearBtn) {
      this.els.clearBtn.addEventListener('click', function () {
        if (self.els.textarea) self.els.textarea.value = '';
        self.lineResults = [];
        self.render();
        self.setStatus('');
      });
    }
    if (this.els.addBtn) {
      this.els.addBtn.addEventListener('click', function () {
        self.addToCart();
      });
    }
    if (this.els.autoBtn) {
      this.els.autoBtn.addEventListener('click', function () {
        self.applyAutoSelect();
        self.render();
        self.updateStatusLine();
      });
    }
    if (this.els.game) {
      this.els.game.addEventListener('change', function () {
        self.onGameChange();
      });
    }
    // Card image preview (delegated so it survives re-renders).
    self.root.addEventListener('click', function (e) {
      var zoom = e.target && e.target.closest ? e.target.closest('[data-bb-db-zoom]') : null;
      if (!zoom || !self.root.contains(zoom)) return;
      e.preventDefault();
      e.stopPropagation();
      openLightbox(zoom.getAttribute('data-bb-db-zoom'), zoom.getAttribute('data-zoom-alt') || '', zoom);
    });
    // 'change' only: checkboxes/selects fire both events in some browsers.
    self.root.addEventListener('change', function (e) {
      var t = e.target;
      if (!t || !self.lineResults.length) return;
      if (t.matches('[data-bb-db-game]')) return; // handled below
      if (t.matches('[data-bb-db-sideboard]')) {
        // Sideboard changes which lines exist, so re-parse and search again.
        self.runSearch();
        return;
      }
      if (t.matches('[data-bb-db-condition], [data-bb-db-instock], [data-bb-db-sort], [data-bb-db-foil]')) {
        self.refilterExisting();
        self.render();
        self.updateStatusLine();
      }
    });
  };

  DeckBuilder.prototype.onGameChange = function () {
    this.applyGameUi();
    var had = this.lineResults.length;
    this.lineResults = [];
    this.render();
    this.setStatus('');
    if (had && this.els.textarea && this.els.textarea.value.trim()) this.runSearch();
  };

  DeckBuilder.prototype.setStatus = function (msg, kind) {
    if (!this.els.status) return;
    this.els.status.textContent = msg || '';
    this.els.status.classList.remove('is-busy', 'is-error');
    if (kind === 'busy') this.els.status.classList.add('is-busy');
    if (kind === 'error') this.els.status.classList.add('is-error');
  };

  DeckBuilder.prototype.updateStatusLine = function () {
    var all = this.lineResults;
    if (!all.length) return;
    var skippedN = all.filter(function (r) {
      return r.status === 'skipped';
    }).length;
    var rows = all.filter(function (r) {
      return r.status !== 'skipped';
    });
    var found = rows.filter(function (r) {
      return r.status !== 'not_found';
    }).length;
    var short = rows.filter(function (r) {
      return r.status === 'partial';
    }).length;
    this.setStatus(
      'Done — ' + found + ' of ' + rows.length + ' lines matched' + (short ? ', ' + short + ' short on stock' : '') +
        (skippedN ? ', ' + skippedN + ' basic Energy skipped' : '') + '.'
    );
  };

  DeckBuilder.prototype.setProgress = function (done, total) {
    if (!this.els.progress || !this.els.progressBar) return;
    if (!total) {
      this.els.progress.classList.remove('is-on');
      this.els.progressBar.style.width = '0%';
      return;
    }
    this.els.progress.classList.add('is-on');
    this.els.progressBar.style.width = Math.round((done / total) * 100) + '%';
  };

  DeckBuilder.prototype.runSearch = async function () {
    if (this.busy) return;
    var text = this.els.textarea ? this.els.textarea.value : '';
    var state = this.getState();
    var lines = parseDeckList(text, state.includeSideboard, state.game);
    if (!lines.length) {
      this.setStatus('Paste a deck list first.', 'error');
      return;
    }

    this.busy = true;
    if (this.els.findBtn) this.els.findBtn.disabled = true;
    this.setStatus('Searching ' + lines.length + ' unique lines…', 'busy');
    this.setProgress(0, lines.length);

    var self = this;
    var done = 0;
    try {
      var results = await mapPool(lines, CONCURRENCY, async function (line) {
        var row = await self.resolveLine(line, state);
        done += 1;
        self.setProgress(done, lines.length);
        self.setStatus('Searching… ' + done + ' / ' + lines.length, 'busy');
        return row;
      });
      this.lineResults = results;
      // Filters may have changed while searching; rebuild against current state.
      this.refilterExisting();
      this.render();
      this.updateStatusLine();
    } catch (err) {
      console.error(err);
      this.setStatus((err && err.message) || 'Search failed.', 'error');
    } finally {
      this.busy = false;
      if (this.els.findBtn) this.els.findBtn.disabled = false;
      this.setProgress(0, 0);
    }
  };

  DeckBuilder.prototype.resolveLine = async function (line, state) {
    if (line.skip) {
      return { line: line, products: [], candidates: [], picks: [], pinnedId: null, qty: line.qty, status: 'skipped', skipped: line.skip };
    }
    var game = getGame(state.game);
    var products = await this.findProductsForLine(line, game);
    if (line.codeOnly && products.length) {
      // "4xOP01-016" -> show "Nami (OP01-016)"
      var nm = game.cardName(products[0].title || '');
      var tag = line.code || (line.setCode + ' ' + line.collector);
      if (nm) line.displayName = nm + ' (' + tag + ')';
    }
    return {
      line: line,
      products: products,
      candidates: buildCandidateVariants(products, line, state),
      picks: [],
      pinnedId: null,
      qty: line.qty,
      status: 'not_found'
    };
  };

  /** Search queries for a line, most specific first. MTG queries are unchanged. */
  function queriesForLine(line, game) {
    var want = line.name;
    var queries = [];
    if (game.id === 'mtg') {
      if (line.setCode && line.collector) {
        queries.push(want + ' ' + line.setCode + '-' + line.collector);
        queries.push(want + ' (' + line.setCode + '-' + line.collector + ')');
      }
      if (line.setCode) queries.push(want + ' ' + line.setCode);
      queries.push('"' + want + '"');
      queries.push(want);
      return queries;
    }
    var searchName = want;
    if (game.id === 'pokemon' && want) {
      // "{R}" energy symbols aren't in titles; search the plain word.
      searchName = want.replace(/\{([A-Za-z])\}/g, function (m, c) {
        var t = ENERGY_SYMBOLS[c.toUpperCase()];
        return t ? t.charAt(0).toUpperCase() + t.slice(1) : m;
      });
    }
    if (game.usesCodes && line.code) {
      queries.push(line.code);
      if (searchName) queries.push(searchName + ' ' + line.code);
      return queries;
    }
    var setLabel = line.setCode && game.setLabel ? game.setLabel(line.setCode) : null;
    if (line.codeOnly) {
      if (setLabel && line.collector) queries.push(setLabel + ' ' + line.collector);
      if (line.collector) queries.push(String(line.collector));
      return queries;
    }
    if (line.collector) {
      var n = normCollector(line.collector);
      var padded = /^\d+$/.test(n) ? ('00' + n).slice(-3) : n;
      queries.push(searchName + ' ' + padded);
      if (padded !== n) queries.push(searchName + ' ' + n);
    }
    if (setLabel) queries.push(searchName + ' ' + setLabel);
    queries.push('"' + searchName + '"');
    queries.push(searchName);
    return queries;
  }

  DeckBuilder.prototype.findProductsForLine = async function (line, game) {
    game = game || GAMES.mtg;
    var isMtg = game.id === 'mtg';
    var want = line.name;
    var queries = queriesForLine(line, game);
    var wantsPrinting = !!(line.setCode || line.collector || line.code);
    function matches(p) {
      return isMtg ? game.isSingle(p) && exactNameMatch(p, want) : lineMatchesProduct(p, line, game);
    }

    var byId = Object.create(null);
    var exact = [];
    function hasPreferred() {
      return exact.some(function (p) {
        // Pokémon: an out-of-stock exact printing keeps searching so Trainer
        // fallbacks have other printings to choose from.
        return scorePrinting(p, line, game) >= 50 && (game.id !== 'pokemon' || p.available !== false);
      });
    }

    for (var i = 0; i < queries.length; i++) {
      var found = await suggestProducts(queries[i]);
      found.forEach(function (p) {
        if (!p || !p.handle) return;
        if (byId[p.id]) return;
        byId[p.id] = true;
        if (!matches(p)) return;
        exact.push(p);
      });
      if (isMtg) {
        if (exact.length >= 8 && (line.setCode || line.collector)) break;
      } else if (wantsPrinting && hasPreferred()) {
        break;
      }
    }

    // If few printings, scrape the HTML search for more handles.
    var needMore = isMtg
      ? exact.length < 8 && !line.setCode
      : exact.length < 8 && !(wantsPrinting && hasPreferred());
    if (needMore) {
      try {
        var htmlQuery = isMtg ? '"' + want + '"' : queries[queries.length - 1];
        var hint = isMtg ? normalizeNameKey(want) : (line.code || want || queries[0] || '').toLowerCase();
        var handles = await searchHandlesFromHtml(isMtg ? htmlQuery : htmlQuery.replace(/^"|"$/g, ''), hint);
        var missing = handles.filter(function (h) {
          return !exact.some(function (p) {
            return p.handle === h;
          });
        }).slice(0, 24);
        var extras = await mapPool(missing, CONCURRENCY, function (h) {
          return fetchProduct(h).catch(function () {
            return null;
          });
        });
        extras.forEach(function (p) {
          if (!p) return;
          if (!matches(p)) return;
          if (byId[p.id]) return;
          byId[p.id] = true;
          exact.push(p);
        });
      } catch (e) {
        /* ignore scrape failures */
      }
    }

    // Hydrate variants via product.js (suggest omits them), then stock counts
    // for anything sellable via the bb-stock-json section.
    var hydrated = await mapPool(exact, CONCURRENCY, async function (p) {
      var full = p;
      if (!(p.variants && p.variants.length)) {
        try {
          full = (await fetchProduct(p.handle)) || p;
          // Keep suggest image/tags if useful
          full.tags = full.tags || p.tags;
        } catch (e) {
          full = p;
        }
      }
      var sellable = (full.variants || []).some(function (v) {
        return v.available;
      });
      if (sellable && full.handle) full.__bbStock = await fetchStock(full.handle);
      return full;
    });

    // Non-MTG: keep only the best-matching printing tier (set+number beats set-only beats number-only).
    if (!isMtg && wantsPrinting) {
      var tierOf = function (p) {
        return Math.floor(scorePrinting(p, line, game) / 50);
      };
      var best = hydrated.reduce(function (m, p) {
        return Math.max(m, tierOf(p));
      }, 0);
      if (game.strictPrinting) {
        // Pokémon: the exact printing is the given number in the given set
        // (an unknown set code falls back to number only).
        var exactOf = function (p) {
          var meta = game.meta(p);
          var numOk = !line.collector || collectorEq(line.collector, meta.collector);
          var setOk = !line.setCode || !game.setLabel(line.setCode) || game.setMatches(meta, line.setCode);
          return numOk && setOk;
        };
        if (game.fallbackEligible && game.fallbackEligible(line, hydrated)) {
          // Trainer / Special Energy: keep every same-name printing; the exact
          // printing wins while it has a usable copy (see buildCandidateVariants).
          line.allowFallback = true;
          hydrated.forEach(function (p) {
            p.__bbBest = exactOf(p);
          });
          return hydrated.filter(Boolean);
        }
        // Pokémon (creature) cards stay strict: wrong printing = not found.
        return hydrated.filter(exactOf);
      }
      if (best >= 1) {
        return hydrated.filter(function (p) {
          return tierOf(p) === best;
        });
      }
      return hydrated.filter(Boolean);
    }

    // Prefer set/collector when requested
    if (line.setCode || line.collector || line.code) {
      var preferred = hydrated.filter(function (p) {
        return scorePrinting(p, line, game) >= 100;
      });
      if (preferred.length) return preferred;
      preferred = hydrated.filter(function (p) {
        return scorePrinting(p, line, game) >= 50;
      });
      if (preferred.length) return preferred;
    }

    return hydrated.filter(Boolean);
  };

  /** Rebuild every line from its cached products under current filters, then re-run selection. */
  DeckBuilder.prototype.refilterExisting = function () {
    var state = this.getState();
    var self = this;
    this.lineResults.forEach(function (row) {
      if (row.status === 'skipped') return;
      row.candidates = buildCandidateVariants(row.products || [], row.line, state);
      if (row.pinnedId) {
        var stillOk = row.candidates.some(function (c) {
          return c.variant.id === row.pinnedId && c.available;
        });
        if (!stillOk) row.pinnedId = null;
      }
      self.allocateRow(row);
    });
  };

  DeckBuilder.prototype.allocateRow = function (row) {
    row.picks = allocate(row.candidates, row.line, row.qty || row.line.qty || 1, row.pinnedId);
    this.updateRowStatus(row);
  };

  DeckBuilder.prototype.applyAutoSelect = function () {
    var self = this;
    this.lineResults.forEach(function (row) {
      if (row.status === 'skipped') return;
      row.pinnedId = null;
      self.allocateRow(row);
    });
  };

  /** found = full qty covered; partial = printings exist but not enough in stock; not_found = no printing passes filters. */
  DeckBuilder.prototype.updateRowStatus = function (row) {
    var want = row.qty || row.line.qty || 1;
    row.allocated = pickedQty(row);
    if (!row.candidates.length) row.status = 'not_found';
    else if (row.allocated >= want) row.status = 'found';
    else row.status = 'partial';
  };

  DeckBuilder.prototype.render = function () {
    if (!this.els.results || !this.els.lines) return;
    if (!this.lineResults.length) {
      this.els.results.hidden = true;
      this.els.lines.innerHTML = '';
      if (this.els.notfound) this.els.notfound.innerHTML = '';
      if (this.els.summary) this.els.summary.innerHTML = '';
      if (this.els.total) this.els.total.textContent = money(0);
      return;
    }
    this.els.results.hidden = false;

    var self = this;
    var html = '';
    var notFound = [];
    var shortCount = 0;
    var selectedCount = 0;
    var totalCents = 0;
    var skipped = [];
    var matchable = 0;

    this.lineResults.forEach(function (row, idx) {
      if (row.status === 'skipped') {
        skipped.push(row.line);
        return;
      }
      matchable += 1;
      if (row.status === 'not_found') notFound.push(row.line);
      if (row.status === 'partial') shortCount += 1;
      if (row.picks && row.picks.length) {
        selectedCount += 1;
        row.picks.forEach(function (p) {
          totalCents += p.cand.price * p.qty;
        });
      }
      html += self.renderLine(row, idx);
    });

    this.els.lines.innerHTML = html;
    if (this.els.notfound) {
      if (notFound.length) {
        this.els.notfound.innerHTML =
          '<div class="bb-db__notfound"><strong>Not found (' +
          notFound.length +
          ')</strong><ul>' +
          notFound
            .map(function (l) {
              var extra = '';
              if (l.setCode) extra += ' [' + l.setCode + (l.collector ? ' ' + l.collector : '') + ']';
              return '<li>' + escapeHtml(l.qty + ' ' + (l.displayName || l.name) + extra) + (l.sideboard ? ' <em>(SB)</em>' : '') + '</li>';
            })
            .join('') +
          '</ul></div>';
      } else {
        this.els.notfound.innerHTML = '';
      }
      if (skipped.length) {
        this.els.notfound.innerHTML +=
          '<div class="bb-db__skipped"><strong>Skipped — basic Energy (' +
          skipped.length +
          ')</strong><ul>' +
          skipped
            .map(function (l) {
              return '<li>' + escapeHtml(l.qty + ' ' + (l.displayName || l.name)) + '</li>';
            })
            .join('') +
          '</ul><p class="bb-db__hint">Basic Energy isn’t matched as singles. Ask us at the counter or add it from your own collection.</p></div>';
      }
    }
    if (this.els.summary) {
      this.els.summary.innerHTML =
        '<span><strong>' +
        selectedCount +
        '</strong> selected</span>' +
        '<span>' +
        matchable +
        ' lines</span>' +
        (shortCount ? '<span class="bb-db__badge bb-db__badge--warn">' + shortCount + ' short</span>' : '') +
        '<span class="bb-db__badge ' + (notFound.length ? 'bb-db__badge--err' : 'bb-db__badge--ok') + '">' +
        notFound.length +
        ' missing</span>';
    }
    if (this.els.total) this.els.total.textContent = money(totalCents);
    if (this.els.addBtn) this.els.addBtn.disabled = selectedCount === 0;

    this.bindLineEvents();
  };

  DeckBuilder.prototype.renderLine = function (row, idx) {
    var line = row.line;
    var want = row.qty || line.qty || 1;
    var badge =
      row.status === 'found'
        ? '<span class="bb-db__badge bb-db__badge--ok">Found</span>'
        : row.status === 'partial'
          ? '<span class="bb-db__badge bb-db__badge--warn">' +
            (row.allocated ? 'Partial: ' + row.allocated + ' of ' + want + ' available' : 'Out of stock') +
            '</span>'
          : '<span class="bb-db__badge bb-db__badge--err">Not found</span>';
    if (row.status !== 'not_found' && row.candidates.length && row.candidates.every(function (c) {
      return c.fallback;
    })) {
      badge +=
        '<span class="bb-db__badge bb-db__badge--warn bb-db__badge--fallback" title="The exact printing in your list isn’t in stock, so we matched another printing of the same card.">Different printing</span>';
    }
    var setHint = '';
    if (line.setCode) {
      setHint =
        '<span class="bb-db__line-meta">Want ' +
        escapeHtml(line.code || line.setCode) +
        (line.collector && !line.code ? ' #' + escapeHtml(line.collector) : '') +
        '</span>';
    }
    var pickById = Object.create(null);
    (row.picks || []).forEach(function (p) {
      pickById[p.cand.variant.id] = p;
    });
    var variantsHtml = '';
    if (row.candidates.length) {
      // First 12 in sort order, plus any picked printing that sorted further down.
      var shown = row.candidates.slice(0, 12);
      row.candidates.slice(12).forEach(function (c) {
        if (pickById[c.variant.id]) shown.push(c);
      });
      variantsHtml =
        '<div class="bb-db__variants">' +
        shown
          .map(function (c) {
            var pick = pickById[c.variant.id];
            var imgSrc = c.image ? (typeof c.image === 'string' ? c.image : c.image.src || c.image.url || '') : '';
            var img = imgSrc
              ? '<button type="button" class="bb-db__thumb" data-bb-db-zoom="' +
                escapeAttr(largeImageUrl(imgSrc, 800)) +
                '" data-zoom-alt="' +
                escapeAttr(c.product.title) +
                '" aria-label="' +
                escapeAttr('Enlarge image: ' + c.product.title) +
                '" aria-haspopup="dialog" title="Enlarge image"><img src="' +
                escapeAttr(thumbImageUrl(imgSrc)) +
                '" alt="" loading="lazy" width="56" height="78"><span class="bb-db__thumb-zoom" aria-hidden="true">⤢</span></button>'
              : '<span class="bb-db__thumb bb-db__thumb--empty" aria-hidden="true"></span>';
            var sub =
              escapeHtml(c.condition || c.variant.title || '') +
              (c.finishLabel ? ' · ' + escapeHtml(c.finishLabel) : '') +
              (!c.available ? ' · OOS' : typeof c.stock === 'number' && isFinite(c.stock) ? ' · ' + c.stock + ' in stock' : '') +
              (pick ? ' · <strong>Using ' + pick.qty + '</strong>' + (pick.unverified ? ' (stock unverified)' : '') : '');
            return (
              '<div class="bb-db__variant-wrap' +
              (pick ? ' is-selected' : '') +
              (c.available ? '' : ' is-disabled') +
              '">' +
              img +
              '<button type="button" class="bb-db__variant' +
              (pick ? ' is-selected' : '') +
              (c.available ? '' : ' is-disabled') +
              '" data-bb-db-pick="' +
              idx +
              '" data-variant-id="' +
              c.variant.id +
              '"' +
              (c.available ? '' : ' disabled') +
              '>' +
              '<span class="bb-db__variant-body"><span class="bb-db__variant-name">' +
              escapeHtml(c.product.title) +
              '</span><span class="bb-db__variant-sub">' +
              sub +
              '</span></span>' +
              '<span class="bb-db__variant-price">' +
              money(c.price) +
              '</span></button></div>'
            );
          })
          .join('') +
        (row.candidates.length > 12
          ? '<p class="bb-db__hint">Showing ' + shown.length + ' of ' + row.candidates.length + ' printings/conditions. Refine filters or add a set code to your list.</p>'
          : '') +
        '</div>';
    } else {
      variantsHtml = '<p class="bb-db__empty">No matching printings for current filters.</p>';
    }

    return (
      '<article class="bb-db__line" data-line-idx="' +
      idx +
      '">' +
      '<div class="bb-db__line-head">' +
      '<h3 class="bb-db__line-title">' +
      '<span>' +
      escapeHtml(String(line.qty)) +
      '× ' +
      escapeHtml(line.displayName || line.name) +
      '</span>' +
      badge +
      (line.sideboard ? '<span class="bb-db__badge bb-db__badge--sec">SB</span>' : '') +
      setHint +
      '</h3>' +
      '<label class="bb-db__line-meta">Qty <input class="bb-db__qty" type="number" min="1" max="' +
      MAX_QTY_FALLBACK +
      '" value="' +
      escapeAttr(String(want)) +
      '" data-bb-db-qty="' +
      idx +
      '"></label>' +
      '</div>' +
      variantsHtml +
      '</article>'
    );
  };

  DeckBuilder.prototype.bindLineEvents = function () {
    var self = this;
    $all('[data-bb-db-pick]', this.root).forEach(function (btn) {
      btn.addEventListener('click', function () {
        var idx = parseInt(btn.getAttribute('data-bb-db-pick'), 10);
        var vid = parseInt(btn.getAttribute('data-variant-id'), 10);
        var row = self.lineResults[idx];
        if (!row) return;
        var cand = row.candidates.find(function (c) {
          return c.variant.id === vid;
        });
        if (!cand || !cand.available) return;
        // Clicked printing is used first; any remaining qty fills cheapest-first.
        row.pinnedId = vid;
        self.allocateRow(row);
        self.render();
        self.updateStatusLine();
      });
    });
    $all('[data-bb-db-qty]', this.root).forEach(function (input) {
      input.addEventListener('change', function () {
        var idx = parseInt(input.getAttribute('data-bb-db-qty'), 10);
        var row = self.lineResults[idx];
        if (!row) return;
        var q = parseInt(input.value, 10);
        if (!isFinite(q) || q < 1) q = 1;
        if (q > MAX_QTY_FALLBACK) q = MAX_QTY_FALLBACK;
        row.qty = q;
        self.allocateRow(row);
        self.render();
        self.updateStatusLine();
      });
    });
  };

  /** One cart item per variant (a line split across printings yields several). */
  DeckBuilder.prototype.collectCartItems = function () {
    var byId = Object.create(null);
    var items = [];
    this.lineResults.forEach(function (row) {
      (row.picks || []).forEach(function (p) {
        var id = p.cand.variant.id;
        if (!byId[id]) {
          byId[id] = {
            id: id,
            quantity: 0,
            label: p.cand.product.title + ' — ' + (p.cand.condition || p.cand.variant.title || '')
          };
          items.push(byId[id]);
        }
        byId[id].quantity += p.qty;
      });
    });
    return items;
  };

  async function cartQuantities() {
    try {
      var cart = await fetchJson(rootPath() + 'cart.js');
      var map = Object.create(null);
      ((cart && cart.items) || []).forEach(function (it) {
        map[it.variant_id] = (map[it.variant_id] || 0) + it.quantity;
      });
      return map;
    } catch (e) {
      return null;
    }
  }

  async function postCartAdd(items) {
    var res = await fetch(rootPath() + 'cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: items.map(function (it) {
          return { id: it.id, quantity: it.quantity };
        })
      })
    });
    var body = await res.text();
    var message = '';
    try {
      var j = JSON.parse(body);
      message = j.description || j.message || '';
    } catch (e) {
      message = body.slice(0, 180);
    }
    return { ok: res.ok, status: res.status, message: message };
  }

  DeckBuilder.prototype.addToCart = async function () {
    var items = this.collectCartItems();
    if (!items.length) {
      this.setStatus('Nothing selected to add.', 'error');
      return;
    }
    var wanted = items.reduce(function (n, it) {
      return n + it.quantity;
    }, 0);

    if (this.els.addBtn) this.els.addBtn.disabled = true;
    this.setStatus('Adding ' + wanted + ' cards to cart…', 'busy');
    try {
      var before = (await cartQuantities()) || Object.create(null);
      var res = await postCartAdd(items);
      var shortages = [];

      if (!res.ok) {
        if (res.status !== 422) throw new Error('Cart error (' + res.status + '): ' + res.message);
        // A 422 can mean nothing or only part was added. Diff the cart, then
        // retry each item that's still short on its own.
        var now = (await cartQuantities()) || before;
        for (var i = 0; i < items.length; i++) {
          var it = items[i];
          var got = (now[it.id] || 0) - (before[it.id] || 0);
          if (got >= it.quantity) continue;
          var r = await postCartAdd([{ id: it.id, quantity: it.quantity - got }]);
          if (r.ok) continue;
          var after = (await cartQuantities()) || now;
          got = (after[it.id] || 0) - (before[it.id] || 0);
          if (got < it.quantity) {
            shortages.push({ label: it.label, wanted: it.quantity, got: Math.max(0, got), message: r.message });
          }
        }
      }

      try {
        document.dispatchEvent(new CustomEvent('cart:updated'));
        document.dispatchEvent(new CustomEvent('theme:cart:change'));
      } catch (e) {}

      if (shortages.length) {
        var missing = shortages.reduce(function (n, s) {
          return n + (s.wanted - s.got);
        }, 0);
        this.setStatus(
          'Added ' + (wanted - missing) + ' of ' + wanted + ' cards. Short on stock: ' +
            shortages
              .map(function (s) {
                return s.label + ' (got ' + s.got + ' of ' + s.wanted + ')';
              })
              .join('; ') +
            '. Run Find Cards again to refresh stock, or open your cart to review.',
          'error'
        );
        this.lastShortages = shortages;
        if (this.els.addBtn) this.els.addBtn.disabled = false;
        return;
      }

      this.setStatus('Added to cart. Opening cart…');
      window.location.href = rootPath() + 'cart';
    } catch (err) {
      console.error(err);
      this.setStatus((err && err.message) || 'Could not add to cart.', 'error');
      if (this.els.addBtn) this.els.addBtn.disabled = false;
    }
  };

  /* ---------- Card image preview (lightbox) ---------- */

  /** Shopify CDN URL at a given width: strips "_200x" style suffixes and sets ?width=. */
  function largeImageUrl(src, width) {
    var u = String(src || '');
    if (!u) return u;
    if (u.indexOf('//') === 0) u = 'https:' + u;
    u = u.replace(/_(?:\d+x\d*|\d*x\d+|pico|icon|thumb|small|compact|medium|large|grande|original|master)(@\dx)?(?=\.(?:jpe?g|png|gif|webp|avif)(?:\?|$))/i, '');
    try {
      var url = new URL(u, window.location.href);
      url.searchParams.delete('height');
      url.searchParams.delete('crop');
      url.searchParams.set('width', String(width || 800));
      return url.toString();
    } catch (e) {
      return u + (u.indexOf('?') >= 0 ? '&' : '?') + 'width=' + (width || 800);
    }
  }

  function thumbImageUrl(src) {
    return /cdn\.shopify\.com|\/cdn\/shop\//.test(String(src)) ? largeImageUrl(src, 160) : src;
  }

  var lightbox = null;

  function buildLightbox() {
    var el = document.createElement('div');
    el.className = 'bb-db-lightbox';
    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-modal', 'true');
    el.setAttribute('aria-label', 'Card image preview');
    el.hidden = true;
    el.innerHTML =
      '<div class="bb-db-lightbox__frame">' +
      '<button type="button" class="bb-db-lightbox__close" data-bb-db-lb-close aria-label="Close preview">×</button>' +
      '<img class="bb-db-lightbox__img" alt="">' +
      '<p class="bb-db-lightbox__caption"></p>' +
      '</div>';
    el.addEventListener('click', function (e) {
      // Click on the backdrop (outside the image) or the X closes.
      if (e.target === el || (e.target.closest && e.target.closest('[data-bb-db-lb-close]'))) closeLightbox();
    });
    el.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' || e.key === 'Esc') {
        e.preventDefault();
        closeLightbox();
      } else if (e.key === 'Tab') {
        // Only one focusable control: keep focus on the close button.
        e.preventDefault();
        el.querySelector('.bb-db-lightbox__close').focus();
      }
    });
    document.addEventListener('keydown', function (e) {
      if ((e.key === 'Escape' || e.key === 'Esc') && lightbox && !lightbox.hidden) closeLightbox();
    });
    document.body.appendChild(el);
    return el;
  }

  function openLightbox(src, alt, opener) {
    if (!src) return;
    if (!lightbox) lightbox = buildLightbox();
    var img = lightbox.querySelector('.bb-db-lightbox__img');
    var cap = lightbox.querySelector('.bb-db-lightbox__caption');
    img.removeAttribute('src');
    img.alt = alt || 'Card image';
    img.src = src;
    cap.textContent = alt || '';
    lightbox.__opener = opener || document.activeElement;
    lightbox.hidden = false;
    document.documentElement.classList.add('bb-db-lightbox-open');
    lightbox.querySelector('.bb-db-lightbox__close').focus();
  }

  function closeLightbox() {
    if (!lightbox || lightbox.hidden) return;
    lightbox.hidden = true;
    document.documentElement.classList.remove('bb-db-lightbox-open');
    var o = lightbox.__opener;
    lightbox.__opener = null;
    if (o && o.focus && document.contains(o)) o.focus();
  }

  function escapeHtml(s) {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function escapeAttr(s) {
    return escapeHtml(s).replace(/'/g, '&#39;');
  }

  function init() {
    $all(ROOT_SEL).forEach(function (root) {
      if (root.__bbDeckBuilder) return;
      root.__bbDeckBuilder = new DeckBuilder(root);
    });
  }

  // Exposed for QA / test harness only.
  window.BBDeckBuilder = {
    DeckBuilder: DeckBuilder,
    parseDeckList: parseDeckList,
    buildCandidateVariants: buildCandidateVariants,
    allocate: allocate,
    fetchStock: fetchStock,
    games: GAMES,
    largeImageUrl: largeImageUrl,
    openLightbox: openLightbox,
    closeLightbox: closeLightbox
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
